import "./ServiceFeedbackForm.css"
import { CCol, CInput, CRow, CTextarea } from "@coreui/react"
import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import { getIncidentDetails } from "extensions/usoc/api/service"
import { getServiceRequestDetails } from "extensions/usoc/api/service"
import { postServiceFeedback } from "extensions/usoc/api/service"
import useSWR from "swr"
import { useExtension, useUserAuth } from "utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar as faSolidStar } from "@fortawesome/free-solid-svg-icons" // Import solid star
import { faStar as faRegularStar } from "@fortawesome/free-regular-svg-icons" // Import regular (outlined) star
import { CSpinner } from "@coreui/react" // Import CSpinner

async function fetchIncidentDetails(auth, extension, projectId, params) {
  const host = extension?.config?.env?.clientApiGateway?.URL
  const results = await getIncidentDetails({ auth, host }, projectId, params).catch((err) => {
    throw err
  })
  return results
}

async function fetchServiceRequestDetails(auth, extension, projectId, param) {
  const host = extension?.config?.env?.clientApiGateway?.URL
  const results = await getServiceRequestDetails(
    { auth, host },
    projectId,
    param
  )
  if (!results) {
    console.error("No results found")
    return null
  }
  return results
}
export default function FeedbackForm() {
  const auth = useUserAuth()
  const extension = useExtension()
  const [rating, setRating] = useState(0)
  const [feedback, setFeedback] = useState("")
  const [ticketData, setTicketData] = useState(null)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false) // Add this line

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const ticket_id = searchParams.get("ticket_id") || null
  const projectID = useSelector((state) => state.settings.currentProject.id)
  const param = `&ticket_id=${ticket_id}`
  const { data: incidentData, error: incidentError } = useSWR(
    () =>
      ticket_id && projectID && ticket_id.startsWith("INC")
        ? `${ticket_id}/projects/${projectID}/get_incident_details?${param}`
        : null,
    () => fetchIncidentDetails(auth, extension, projectID, param)
  )
  const { data: serviceRequestData, error: serviceRequestError } = useSWR(
    () =>
      ticket_id && projectID && ticket_id.startsWith("REQ")
        ? `${ticket_id}/projects/${projectID}/get_service_request_details?${param}`
        : null,
    () => fetchServiceRequestDetails(auth, extension, projectID, param)
  )
  useEffect(() => {
    if (!ticket_id) {
      console.error("No ticket ID provided in URL")
      return
    }
    // Update state based on fetched data
    if (incidentError) {
      console.error("Error fetching incident details:", incidentError)
    } else if (incidentData) {
      setTicketData(incidentData.data[0])
    }
    if (serviceRequestError) {
      console.error(
        "Error fetching service request details:",
        serviceRequestError
      )
    } else if (serviceRequestData) {
      setTicketData(serviceRequestData.data[0])
    }
  }, [
    ticket_id,
    projectID,
    incidentData,
    incidentError,
    serviceRequestData,
    serviceRequestError
  ]) // Add all relevant dependencies
  const handleRatingChange = (selectedStar) => {
    if (rating === selectedStar) {
      setRating(selectedStar - 1)
    } else {
      setRating(selectedStar)
    }
  }
  const handleSubmit = async () => {
    setIsSubmitting(true) // Disable the button and show spinner
    const host = extension?.config?.env?.clientApiGateway?.URL
    const feedbackData = {
      feedback_rating: rating,
      feedback_message: feedback
    }
    try {
      const response = await postServiceFeedback(
        { auth, host, body: JSON.stringify(feedbackData) },
        projectID,
        ticket_id
      )
      if (response.error) {
        console.error("Failed to submit feedback: ", response.error)
      } else {
        setFeedbackSubmitted(true)
      }
    } catch (error) {
      console.error("Error submitting feedback:", error)
    } finally {
      setIsSubmitting(false) // Re-enable the button after submission
    }
  }
  return (
    <div className="feedback-form-container">
      {feedbackSubmitted ? ( // Conditionally render based on feedback submission
        <h4>Thank you for your feedback!</h4>
      ) : ticketData ? (
        <>
          <h2>Ticket Details</h2>
          <CRow aria-label="Ticket ID">
            <CCol md="12">
              <p>
                <strong>Ticket ID:</strong> {ticket_id}
              </p>
            </CCol>
          </CRow>
          <CRow aria-label="Ticket description">
            <CCol md="12">
              <p>
                <strong>Description:</strong> {ticketData.short_description}
              </p>
            </CCol>
          </CRow>
          <CRow aria-label="Ticket resolution information">
            <CCol md="12">
              <p>
                <strong>Resolution:</strong> {ticketData.resolution_notes}
              </p>
            </CCol>
          </CRow>
          <hr />
          <CRow aria-label="Feedback score">
            <CCol md="12">
              <p className="field-label font-weight-bold">
                Please rate your experience with the service from 1 - 5:
              </p>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <div
                    key={star}
                    className="star-container"
                    style={{ textAlign: "center" }}
                    aria-label={`Rate ${star} stars`}
                  >
                    <FontAwesomeIcon
                      icon={rating >= star ? faSolidStar : faRegularStar} // Toggle between solid and outlined
                      onClick={() => handleRatingChange(star)}
                      style={{
                        display: "inline-block",
                        width: "30px",
                        textAlign: "center",
                        cursor: "pointer",
                        color: rating >= star ? "gold" : "black"
                      }}
                      size="2xl" // Adjust size as needed
                      color="black" // Set the desired color
                    />
                  </div>
                ))}
              </div>
            </CCol>
          </CRow>
          <CRow className="my-4">
            <CCol md="12">
              <p className="field-label font-weight-bold">Feedback:</p>
              <CTextarea
                className="feedback-text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                aria-label="Supply any feedback here"
                placeholder="Please provide any feedback you may have here."
              />
            </CCol>
          </CRow>
          <CRow>
            <CCol md="12">
              <button
                className="submit-button"
                onClick={handleSubmit}
                disabled={isSubmitting} // Disable the button if submitting
              >
                {isSubmitting ? ( // Conditionally render button content
                  <CSpinner size="sm" color="light" /> // Show spinner if submitting
                ) : (
                  "Submit"
                )}
              </button>
            </CCol>
          </CRow>
        </>
      ) : (
        <p>Loading ticket data...</p>
      )}
    </div>
  )
}
